// Angular
import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
// Environments
import { environment } from '@env/environment';
// PrimeNG
import { SkeletonModule } from 'primeng/skeleton';
// Services
import { ScriptService } from '@core/utils-v2/script/script.service';

const PRIME_MODULES = [SkeletonModule];
const NG_MODULES = [GoogleMapsModule];

@Component({
  selector: 'app-map-v2',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES],
  templateUrl: './map-v2.component.html',
})
export class MapComponentV2 implements AfterViewInit {
  //#region Inputs & Outputs
  @Output() mapDragend = new EventEmitter<google.maps.MapMouseEvent>();
  @Output() isMapReady = new EventEmitter<void>();
  //#endregion

  countryCode = environment.country;

  isMapLoaded = signal<boolean>(false);
  isPlaceLoaded = signal<boolean>(false);
  isMapScriptLoaded = signal<boolean>(false);
  markerPositions = signal<google.maps.marker.AdvancedMarkerElement[]>([]);
  markerOptions: google.maps.MarkerOptions = {
    draggable: environment.googleMaps.draggable,
  };
  options = signal<google.maps.MapOptions>({
    zoom: 4,
    mapId: '473f291830f1a34c',
    center: environment.googleMaps.center,
  });
  advancedMarkerElement!: typeof google.maps.marker.AdvancedMarkerElement;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private readonly scriptService: ScriptService
  ) {}

  /**
   * Maneja el estado cuando el script está cargado.
   * - Marca el script como cargado.
   * - Inicializa el servicio de Google Places.
   */
  /*private async handleScriptLoaded(): Promise<void> {
    try {
      // Pequeño delay para asegurar que el script se inicialice
      await new Promise((resolve) => setTimeout(resolve, this.INIT_DELAY));

      // Verificar que google esté definido
      if (typeof google === 'undefined') {
        throw new Error('Google Maps API no está disponible');
      }

      this.isMapScriptLoaded.set(true);
      await this.initGooglePlacesService();
    } catch (error) {
      console.error('Error en handleScriptLoaded:', error);
      this.isMapScriptLoaded.set(false);
    }
  }*/

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const response = await this.scriptService.loadScript(
        environment.gmapScript /*,
        {
          async: true,
          defer: true,
          id: 'google-maps-script',
          timeout: this.SCRIPT_TIMEOUT,
        }*/
      );
      if (!response.loaded) {
        throw new Error('Error loading Google Maps API');
      }
      await this.initGooglePlacesService();
      this.isMapScriptLoaded.set(true);
    } catch (error) {
      console.error('Error loading Google Maps:', error);
    }
  }

  private async initGooglePlacesService(): Promise<void> {
    try {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        'marker'
      )) as google.maps.MarkerLibrary;

      this.advancedMarkerElement = AdvancedMarkerElement;
      this.isPlaceLoaded.set(true);
      this.onMapReady();
    } catch (error) {
      console.error('Error initializing Google Places:', error);
      this.isPlaceLoaded.set(false);
    }
  }

  onMapLoaded(): void {
    this.isMapLoaded.set(true);
    this.onMapReady();
  }

  private onMapReady(): void {
    if (this.isMapLoaded() && this.isPlaceLoaded()) {
      this.isMapReady.emit();
    }
  }

  /**
   * Setear marcador en el mapa y centrar el mapa en la ubicación del marcador.
   * - Este método se llama desde otro componente.
   * @param coords - Coordenadas de la ubicación.
   **/
  setMarker(coords: google.maps.LatLngLiteral): void {
    const marker = new this.advancedMarkerElement({
      position: coords,
    });
    this.markerPositions.set([marker]);
    this.options.update((value) => ({
      ...value,
      zoom: 17,
      center: coords,
    }));
  }
}
